/**
 * Mountain Brook's pink Building Permit Application, drawn clean (ADR 0289).
 *
 * Every box on the paper is here, so the city reads the form it printed. Only
 * the boxes Jonathon filled by hand on 2026-10-07 are filled: the job's
 * address, the owner, the contractor with Lysa as its contact, the state
 * licence, "No" architect supervision, "Repair" ("New Construction" on a new
 * build) with the type of work on the Other line, and the certification — amount, dates, company, Lysa's signature
 * and printed name. Everything else is the city's to fill.
 *
 * Change one, change both: the PDF saved to Docs draws this sheet again in
 * `lib/permitPdf/pink.ts`, and nothing compares the two.
 */

import { permitDateText } from "@roof-hawk/core";

import { Box, Line, type PermitEditor } from "./PermitFields";

export function PermitPinkSheet({ ed }: { ed: PermitEditor }) {
  const { d } = ed;
  return (
    <section
      className="kdoc-page kdoc-pink"
      aria-label="Building permit application"
    >
      <div className="kdoc-top">
        <span>City of Mountain Brook</span>
        <strong className="kdoc-title">BUILDING PERMIT APPLICATION</strong>
        <span className="kdoc-small">
          56 Church Street, Mountain Brook AL 35213
        </span>
      </div>
      <div className="kdoc-fee">
        <p>
          (Permit Fee: Residential-$11.00 per $1000 Commercial-$12.00 per $1000
          plus a $50.00 issuance fee MINIMUM FEE OF $100.00) and a $20.00 State
          Trade Academy flat fee
        </p>
        <div className="kdoc-rcvd">
          <span>Rcvd by: ______</span>
          <span>Date: ______</span>
        </div>
      </div>
      <p className="kdoc-acct">RV Acct # ____________</p>
      <p className="kdoc-fine kdoc-bold">
        If the scope and/or nature of the work as listed on the original permit
        application changes OR if the cost of construction exceeds the amount
        upon which the permit is based a revised application must be submitted
        and any additional dues owed must paid. (§109-44i)
      </p>
      <p className="kdoc-fine">
        Application is hereby made for a building permit to accomplish the work
        as herein described in accordance with duplicate plans and/or
        specifications necessary for compliance shall be observed and all
        requirements of the building code, zoning code, and all other pertinent
        laws and ordinances of the City of Mountain Brook regulating
        construction shall be complied pursuit of this work whether or not
        specified herein. REINSPECTION FEE $100.00 — 2021 IBC and 2021 IRC
      </p>

      <h3 className="kdoc-h">JOB LOCATION</h3>
      <Line label="Address:">
        {ed.text(
          d.site.street,
          (v) => ed.setSite({ street: v }),
          "Job location address",
          "kdoc-grow",
        )}
      </Line>
      <div className="kdoc-row3">
        <Line label="Legal Description: Lot:" />
        <Line label="Block" />
        <Line label="Survey" />
      </div>

      <h3 className="kdoc-h">IDENTIFICATION</h3>
      <div className="kdoc-grid2 kdoc-boxed">
        <div>
          <Line label="Name of Owner:">
            {ed.text(
              d.owner.name,
              (v) => ed.setOwner({ name: v }),
              "Owner's name",
              "kdoc-grow",
            )}
          </Line>
          <Line label="Address">
            {ed.text(
              d.owner.address,
              (v) => ed.setOwner({ address: v }),
              "Owner's address",
              "kdoc-grow",
            )}
          </Line>
          <div className="kdoc-pair">
            <Line label="City">
              {ed.text(
                d.owner.city,
                (v) => ed.setOwner({ city: v }),
                "Owner's city",
                "kdoc-grow",
              )}
            </Line>
            <Line label="State">
              {ed.text(
                d.owner.state,
                (v) => ed.setOwner({ state: v }),
                "Owner's state",
                "kdoc-short",
              )}
            </Line>
          </div>
          <Line label="Phone">
            {ed.text(
              d.owner.phone,
              (v) => ed.setOwner({ phone: v }),
              "Owner's phone",
              "kdoc-grow",
            )}
          </Line>
        </div>
        <div>
          <Line label="Name of Contractor">
            {ed.text(
              d.contractor.name,
              (v) => ed.setContractor({ name: v }),
              "Contractor's name",
              "kdoc-grow",
            )}
          </Line>
          <Line label="Address">
            {ed.text(
              d.contractor.address,
              (v) => ed.setContractor({ address: v }),
              "Contractor's address",
              "kdoc-grow",
            )}
          </Line>
          <div className="kdoc-pair">
            <Line label="City">
              {ed.text(
                d.contractor.city,
                (v) => ed.setContractor({ city: v }),
                "Contractor's city",
                "kdoc-grow",
              )}
            </Line>
            <Line label="State">
              {ed.text(
                d.contractor.state,
                (v) => ed.setContractor({ state: v }),
                "Contractor's state",
                "kdoc-short",
              )}
            </Line>
          </div>
          <Line label="Phone">
            {ed.text(
              d.contractor.phone,
              (v) => ed.setContractor({ phone: v }),
              "Contractor's phone",
              "kdoc-grow",
            )}
          </Line>
          <div className="kdoc-pair">
            <Line label="Mtn Brook Lic #">
              {ed.text(
                d.contractor.cityLicenseNumber,
                (v) => ed.setContractor({ cityLicenseNumber: v }),
                "Mountain Brook licence number",
                "kdoc-grow",
              )}
            </Line>
            <Line label="State Lic#">
              {ed.text(
                d.contractor.licenseNumber,
                (v) => ed.setContractor({ licenseNumber: v }),
                "State licence number",
                "kdoc-grow",
              )}
            </Line>
          </div>
        </div>
      </div>

      <div className="kdoc-grid2 kdoc-boxed">
        <div>
          <p className="kdoc-label">Plans Drawn By:</p>
          <p>
            <Box /> Architect <Box /> Engineer <Box /> Designer
          </p>
          <Line label="Name:" />
          <Line label="Address:" />
          <Line label="Phone:" />
          <Line label="State Registration #" />
        </div>
        <div>
          <p>
            Is Architect or Engineering supervision included? <Box /> Yes{" "}
            <Box checked /> No
          </p>
          <p>
            <Box /> Architect <Box /> Engineer
          </p>
          <Line label="If yes, by whom?" />
          <Line label="Phone:" />
          <Line label="State Registration#" />
        </div>
      </div>

      <div className="kdoc-grid3 kdoc-boxed">
        <div>
          <p className="kdoc-label">Type Improvement Permit is for:</p>
          <p>
            <Box checked={!!d.newConstruction} /> New Construction
          </p>
          <p>
            <Box /> Addition
          </p>
          <p>
            <Box /> Alterations
          </p>
          <p>
            <Box checked={!d.newConstruction} /> Repair
          </p>
          <p>
            <Box /> Other
          </p>
          <p className="kdoc-underline">
            {ed.text(
              d.workType,
              (v) => ed.set({ workType: v }),
              "Type of work",
              "kdoc-grow",
            )}
          </p>
        </div>
        <div>
          <p className="kdoc-label">Type Construction</p>
          <p>
            <Box /> Fireproof
          </p>
          <p>
            <Box /> Fire Resistive
          </p>
          <p>
            <Box /> Heavy Timber
          </p>
          <p>
            <Box /> Non-Combustible
          </p>
          <p>
            <Box /> Ordinary
          </p>
          <p>
            <Box /> Other ________
          </p>
        </div>
        <div>
          <p className="kdoc-label">Occupancy (Use)</p>
          <div className="kdoc-pair kdoc-top-align">
            <div>
              <p className="kdoc-label">NONRESIDENTIAL</p>
              <p className="kdoc-tiny">
                <Box /> Amusement, Recreational
                <br />
                <Box /> Church/Religious
                <br />
                <Box /> Business
                <br />
                <Box /> Service Station/Repair Garage
                <br />
                <Box /> Hospital/Institutional
                <br />
                <Box /> Office/Professional
                <br />
                <Box /> School/Library/Educational
                <br />
                <Box /> Other
              </p>
            </div>
            <div>
              <p className="kdoc-label">RESIDENTIAL</p>
              <p className="kdoc-tiny">
                <Box /> Single Family
                <br />
                <Box /> Duplex
                <br />
                <Box /> Apartment - # of Units ___
              </p>
            </div>
          </div>
        </div>
      </div>

      <h3 className="kdoc-h">SELECTED CHARACTERISTICS OF BUILDING</h3>
      <div className="kdoc-grid2 kdoc-boxed kdoc-tiny">
        <div>
          <p>
            Type of heat: <Box /> Gas <Box /> Oil <Box /> Electric <Box /> Coal{" "}
            <Box /> Other
          </p>
          <p>
            Type of sewage disposal: <Box /> Sanitary Sewer <Box /> Septic
          </p>
          <p>
            Type of water supply: <Box /> Public <Box /> Individual (well, etc)
          </p>
          <p>
            Will there be central air? <Box /> Yes <Box /> No
          </p>
          <p>
            Will there be an elevator? <Box /> Yes <Box /> No
          </p>
          <p>Will there be off street parking? # of spaces? ____</p>
        </div>
        <div>
          <p className="kdoc-label">Dimensions</p>
          <Line label="Number of stories" />
          <Line label="Total sq ft living area" />
          <Line label="Total sq ft of non-living area" />
        </div>
        <div>
          <p className="kdoc-label">Residential Buildings Only:</p>
          <Line label="Number of Bedrooms" />
          <Line label="Number of Bathrooms" />
          <Line label="Total Number of Rooms" />
        </div>
        <div>
          <p className="kdoc-label">Plot and Zoning Information:</p>
          <Line label="Plot area (in sq feet)" />
          <Line label="Plot width: Front ____ Rear" />
          <Line label="Length of sides 1 ____ 2" />
        </div>
      </div>

      <div className="kdoc-grid2 kdoc-boxed">
        <p className="kdoc-bold">
          SUBCONTRACTORS: Please list ALL subcontractors to be used on job on
          the BACK of this form. List names, address, and phone number of subs
        </p>
        <p className="kdoc-bold">
          By signing below I understand that it is the contractor&apos;s
          responsibility to call the City and schedule all inspections. Failure
          to do so may result in future permit applications being denied.
        </p>
      </div>

      <h3 className="kdoc-h">CERTIFICATION</h3>
      <p className="kdoc-fine">
        I hereby certify that I have read this application and that all
        information contained herein is true and correct, that I agree to comply
        with all city ordinances and state laws regulating building
        construction, that I am the owner or the authorized agent for the work
        herein described and that the total contract or valuations is:
      </p>
      <div className="kdoc-row3">
        <Line label="$">{ed.amount()}</Line>
        <Line label="Proposed Start Date:">
          {ed.date(
            d.proposedStart,
            (v) => ed.set({ proposedStart: v }),
            "Proposed start date",
          )}
        </Line>
        <Line label="Proposed End Date:">
          {ed.date(
            d.proposedEnd,
            (v) => ed.set({ proposedEnd: v }),
            "Proposed end date",
          )}
        </Line>
      </div>
      <div className="kdoc-pair">
        <Line label="Name of Company:">
          <span className="kdoc-val">{d.contractor.name}</span>
        </Line>
        <Line label="Date:">
          <span className="kdoc-val">{permitDateText(d.preparedOn)}</span>
        </Line>
      </div>
      <div className="kdoc-pair">
        <Line label="Signature:">
          <span className="pdoc-hand kdoc-sig">{d.contractor.signerName}</span>
        </Line>
        <Line label="Printed Name:">
          <span className="kdoc-val">{d.contractor.signerName}</span>
        </Line>
      </div>
      <div className="kdoc-office">
        <p className="kdoc-label kdoc-center">For office use only</p>
        <div className="kdoc-row3">
          <Line label="Approved by:" />
          <Line label="Job Cost:" />
          <Line label="Fire Marshal:" />
        </div>
        <div className="kdoc-row3">
          <Line label="Permit Fee:" />
          <Line label="Date Issued:" />
          <Line label="Permit #" />
        </div>
      </div>
    </section>
  );
}
